/**
 * schema/rules/rule.ts
 * The rule domain contract. `Rule` is the UI-normalized shape every screen
 * consumes (services map the backend wire shape + per-rule stats into it).
 * `BuilderDraft` is the guided-authoring model; `RuleBody` is the canonical
 * wire payload the service builds from a draft.
 */
import * as yup from 'yup';

export type RuleStatus = 'draft' | 'approved' | 'active' | 'deactivated';
export type RuleSource = 'ai_proposed' | 'manual';
export type Tone = 'warm' | 'neutral' | 'playful';

/** UI-normalized rule used by the Rules list + Performance join. */
export interface Rule {
  id: string;
  name: string;
  motion: string;
  status: RuleStatus;
  source: RuleSource;
  priority: number;
  /** Plain-English trigger summary (services derive it from the trigger AST). */
  triggerText: string;
  /** Full plain sentence: "When a customer buys X, suggest Y." */
  intentText?: string;
  /** What the rule suggests, in words. */
  fromText: string;
  /** Each category named in the rule → real example items (operator preview). */
  categoryExamples?: Array<{ name: string; items: string[] }>;
  select: string;
  /** From per-rule stats — null while a draft / no signal. */
  acceptRate: number | null;
  trend: number[] | null;
  cold: boolean;
  dormant: boolean;
  /** Why the rule is dormant (suggest-side or trigger-side), shown as a hint. */
  dormantReason?: string;
  /** Soft-deleted — lives only in the Rules "Deleted" tab. */
  deleted?: boolean;
  superseded: boolean;
  supersededBy?: string;
}

export interface CategoryOption {
  value: string;
  label: string;
}

/** Guided builder model — category-shaped, mapped to the canonical AST on save. */
export interface BuilderDraft {
  id: string | null;
  name: string;
  motion: string;
  status: RuleStatus;
  source: RuleSource;
  priority: number;
  triggerCat: string;
  excludeCat: string;
  fromValue: string;
  select: string;
  maxSuggestions: number;
  maxPriceDelta: number;
  mustAvailable: boolean;
  dietaryMatch: boolean;
  phrasing: string;
  tone: Tone;
  isNew: boolean;
}

/* ── Canonical wire payload (POST /v1/rules, PATCH /v1/rules/:id) ──────────── */

export interface TriggerCondition {
  cart_contains_category?: string;
  cart_not_contains_category?: string;
  cart_contains_item?: string;
  cart_total_gte?: number;
}

export interface RuleBody {
  name: string;
  motion: string;
  trigger: { all?: TriggerCondition[]; any?: TriggerCondition[] };
  recommend: {
    from: { category?: string; tags?: string[]; relation?: string; explicit?: string[] };
    select: string;
    max_suggestions: number;
  };
  constraints: {
    must_be_available: boolean;
    max_price_delta_pct?: number;
    compatibility: string[];
    exclude_if_in_cart: boolean;
  };
  phrasing: { template: string; tone: string };
}

/** Builder validation — surfaced inline + via Toast on save. */
export const builderSchema = yup.object({
  name: yup.string().trim().required('Name this rule'),
  priority: yup
    .number()
    .typeError('Priority must be a number')
    .min(0, 'Min 0')
    .max(255, 'Max 255')
    .required(),
  maxPriceDelta: yup
    .number()
    .typeError('Delta must be a number')
    .min(0, 'Min 0%')
    .max(100, 'Max 100%')
    .required(),
});
